import React, { useState, useEffect, useCallback } from 'react';
import { Button, Spin, notification, DatePicker } from 'antd';
import { Table, Modal } from '_src/components';
import { Select, Input } from '_src/shared/form-helpers';
import { useFetch, useSave } from 'store';
import services from '_src/services';
import moment from 'moment';

import { FiltersRow, StatusBadge } from '../styles';

const { RangePicker } = DatePicker;
const laptopConfig = services['laptopLending'];

export default function LaptopLoansTable({ branchId }) {
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    search: '',
    dateRange: null
  });

  const { get: fetchLoans } = useFetch(laptopConfig.getLoans);

  const loadLoans = useCallback(async () => {
    if (!branchId) return;

    setLoading(true);
    try {
      const params = { branch_id: branchId };
      if (filters.status !== 'all') {
        params.status = filters.status;
      }
      if (filters.search) {
        params.search = filters.search;
      }
      if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
        params.date_from = filters.dateRange[0].format('YYYY-MM-DD');
        params.date_to = filters.dateRange[1].format('YYYY-MM-DD');
      }

      const response = await fetchLoans({ params });
      const loanList = response?.loans || response?.[0]?.data?.loans || [];
      setLoans(loanList.map((l, i) => ({ ...l, key: l.id || i })));
    } catch (error) {
      notification.error({ message: 'Failed to load loans', duration: 5 });
    }
    setLoading(false);
  }, [branchId, filters]);

  useEffect(() => {
    loadLoans();
  }, [loadLoans]);

  const getStatusDisplay = (loan) => {
    const returnedAt = loan.returnedAt || loan.returned_at;
    const condition = loan.conditionOnReturn || loan.condition_on_return;
    if (!returnedAt) return 'active';
    if (condition === 'damaged') return 'damaged';
    return 'returned';
  };

  const getStatusLabel = (status) => {
    const labels = {
      active: 'Checked Out',
      returned: 'Returned',
      damaged: 'Returned (Damaged)'
    };
    return labels[status] || status;
  };

  const columns = [
    {
      title: 'Student',
      key: 'student',
      render: (_, record) => {
        const firstName = record.studentFirstName || record.student_first_name || '';
        const lastName = record.studentLastName || record.student_last_name || '';
        if (firstName || lastName) {
          return `${firstName} ${lastName}`.trim();
        }
        return '-';
      }
    },
    {
      title: 'Laptop',
      key: 'laptop',
      render: (_, record) => {
        if (record.brand || record.model) {
          return `${record.brand || ''} ${record.model || ''}`.trim();
        }
        return '-';
      }
    },
    {
      title: 'Serial Number',
      key: 'serial_number',
      render: (_, record) => record.serialNumber || record.serial_number || '-'
    },
    {
      title: 'Issued Date',
      key: 'issued_at',
      render: (_, record) => {
        const val = record.issuedAt || record.issued_at;
        return val ? moment(val).format('MM/DD/YYYY HH:mm') : '-';
      }
    },
    {
      title: 'Returned Date',
      key: 'returned_at',
      render: (_, record) => {
        const val = record.returnedAt || record.returned_at;
        return val ? moment(val).format('MM/DD/YYYY HH:mm') : '-';
      }
    },
    {
      title: 'Issued By',
      key: 'issued_by',
      render: (_, record) => record.issuedByName || record.issued_by_name || '-'
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => {
        const status = getStatusDisplay(record);
        return <StatusBadge status={status === 'active' ? 'checked_out' : status === 'damaged' ? 'maintenance' : 'available'}>
          {getStatusLabel(status)}
        </StatusBadge>;
      }
    },
    {
      title: 'Condition on Return',
      key: 'condition_on_return',
      render: (_, record) => {
        const val = record.conditionOnReturn || record.condition_on_return;
        return val ? val.charAt(0).toUpperCase() + val.slice(1) : '-';
      }
    }
  ];

  return (
    <div>
      <FiltersRow>
        <Input
          label="Search"
          placeholder="Student name, serial number..."
          value={filters.search}
          onChange={value => setFilters(prev => ({ ...prev, search: value }))}
          style={{ width: 250 }}
          allowClear
        />

        <Select
          label="Status"
          value={filters.status}
          onChange={value => setFilters(prev => ({ ...prev, status: value }))}
          options={[
            { id: 'all', label: 'All' },
            { id: 'active', label: 'Currently Checked Out' },
            { id: 'returned', label: 'Returned' }
          ]}
          style={{ width: 200 }}
        />

        <div>
          <div style={{ marginBottom: 5 }}>Date Range</div>
          <RangePicker
            format="MM/DD/YYYY"
            value={filters.dateRange}
            onChange={dates => setFilters(prev => ({ ...prev, dateRange: dates }))}
            allowClear
          />
        </div>

        <Button onClick={loadLoans} style={{ marginLeft: 'auto' }}>
          Refresh
        </Button>
      </FiltersRow>

      <Spin spinning={loading}>
        <Table
          dataSource={loans}
          columns={columns}
          pagination={{ pageSize: 20 }}
        />
      </Spin>
    </div>
  );
}
